import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "./api";

const AuthContext = createContext(null);

// 2026-09-25 (fix logout duplikat): guard module-level — logout() selalu
// diakhiri hard-navigate ke /login, jadi flag tidak perlu di-reset.
let _logoutInFlight = false;

/**
 * Cookie-based auth: token disimpan di HttpOnly cookie oleh backend (tidak dapat
 * diakses JS → immune dari XSS token theft). Frontend hanya cache user profile
 * (non-sensitive) di localStorage supaya boot instant, lalu sync via /auth/me.
 *
 * 149-ar: expose `mustChangePassword` signal → parent renders ForceChangePasswordDialog.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("ens_user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const refreshSecurityStatus = async () => {
    try {
      const { data } = await api.get("/auth/security-status");
      setMustChangePassword(!!data.must_change_password);
    } catch { setMustChangePassword(false); }
  };

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      setUser(data);
      try { localStorage.setItem("ens_user", JSON.stringify(data)); } catch { /* ignore */ }
      refreshSecurityStatus();
    }).catch(() => {
      try { localStorage.removeItem("ens_user"); } catch { /* ignore */ }
      setUser(null);
    }).finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      try { localStorage.setItem("ens_user", JSON.stringify(data)); } catch { /* ignore */ }
      await refreshSecurityStatus();
    } catch { /* ignore */ }
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    try { localStorage.setItem("ens_user", JSON.stringify(data.user)); } catch { /* ignore */ }
    setMustChangePassword(!!data.must_change_password);
    return data.user;
  };

  /**
   * 149-cw-r2 (PROMPT 16-UI-PROFILE-DROPDOWN-R1-R2): Robust logout.
   * Flow:
   *   1. POST /auth/logout        (invalidate server-side session — best effort)
   *   2. sendBeacon presence/offline (matikan realtime presence sebelum navigate)
   *   3. Purge AUTH-only local state (JANGAN sentuh preferensi UI: dashboard
   *      config, table column widths, sidebar collapse, dsb.)
   *   4. Clear React auth context (`setUser(null)`)
   *   5. Warn user kalau server tidak konfirmasi — tetap lanjut clear + navigate
   *   6. `window.location.replace("/login")` — TIDAK menambah entry ke history,
   *      Back button tidak akan expose halaman privat sesi lama.
   *
   * Preservasi preferensi non-keamanan: dashboard config, layout kolom, sidebar
   * pin, project filter, dst. → tetap tersimpan supaya login berikutnya nyaman.
   */
  const logout = async () => {
    // 2026-09-25 (fix logout duplikat): cegah pemanggil ganda (mis. IdleTimeout
    // auto-fire + klik tombol konfirmasi bersamaan, atau double-click) mengirim
    // lebih dari satu POST /auth/logout untuk kejadian yang sama.
    if (_logoutInFlight) return;
    _logoutInFlight = true;
    // 1. Beritahu backend untuk invalidate session (best effort — jaringan bisa
    //    down; kita tetap clear lokal). Cookie akan di-clear via Set-Cookie
    //    dari response 200.
    let serverAck = true;
    try { await api.post("/auth/logout"); }
    catch { serverAck = false; }

    // 2. Presence offline via sendBeacon — reliable meski selama page unload.
    try {
      const base = (api.defaults.baseURL || "").replace(/\/$/, "");
      if (base) navigator.sendBeacon?.(`${base}/messaging/presence/offline`);
    } catch { /* ignore */ }

    // 3. Purge AUTH-only keys. JANGAN hapus preferensi UI/dashboard/layout
    //    kolom — user sudah investasi setup, biar tetap untuk login berikutnya.
    try {
      localStorage.removeItem("ens_user");
      // Legacy/potential token hints from older builds:
      localStorage.removeItem("ens_token");
      localStorage.removeItem("ens_refresh_token");
      localStorage.removeItem("ens_session");
      // Session-scoped auth artefacts (impersonation flags, banner state, dsb.):
      try { sessionStorage.clear(); } catch { /* ignore */ }
    } catch { /* ignore */ }

    // 4. Clear React state (dilakukan sebelum hard-nav sebagai defense-in-depth
    //    jika ada listener yang read auth state saat unmount).
    setUser(null);
    setMustChangePassword(false);

    // 5. Kalau server tidak konfirmasi, tampilkan warning eksplisit sebelum
    //    navigate agar user tahu sesi backend mungkin masih aktif di server
    //    (misal karena network drop). Toast tampil singkat sebelum reload.
    if (!serverAck) {
      try { toast.warning("Server tidak merespons saat logout. Sesi lokal sudah dibersihkan."); }
      catch { /* ignore */ }
    }

    // 6. Hard-navigate dengan REPLACE (bukan href) sehingga Back button tidak
    //    dapat mengembalikan user ke halaman terlindungi sesi lama.
    window.location.replace("/login");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, mustChangePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
